import { HTTP_CP_MortgageCase } from '@api-new/caseservice';
import { HTTP_CP_Mortgage, HTTP_CP_SmartSearch } from '@api-new/mortgageservice';
import { HTTP_CP_Property } from '@api-new/propertyservice';
import { CasePreviewCard } from '@platform/property/models/mortgage-preview-item.model';
import { ENUM_PropertyInvestmentTypeMap } from '@shared/maps';
import { BestBetterDealViewModel } from '@shared/models/best-better-deal-view.model';
import {
  PropertiesPortfolioItem,
  PropertiesPortfolioItemMortgage,
  PropertiesPortfolioItemMortgageCase,
  PropertiesPortfolioItemProperty,
} from '../../models/properties-portfolio-item.model';
import { buildPortfolioCasePreviewCard } from './portfolio-case-preview-card.adapter';

export const selectPortfolioPropertyAdapter = (
  property: HTTP_CP_Property,
  mortgages: HTTP_CP_Mortgage[],
  bestBetterDeals: BestBetterDealViewModel[],
  mortgageCases: HTTP_CP_MortgageCase[],
  smartSearches: HTTP_CP_SmartSearch[],
): PropertiesPortfolioItem => {
  const mortgage = mortgages.find((mortgage) => mortgage.propertyId === property.id);
  const bestMortgageDeals = findBestDealsByMortgageId(mortgage?.id, bestBetterDeals);
  const mortgageCase = findMortgageCase(mortgage?.id, mortgageCases);
  const casePreviewCard: CasePreviewCard = buildPortfolioCasePreviewCard(mortgageCase, bestMortgageDeals?.numberOfMortgageDeals);
  const bestBetterDeal = bestMortgageDeals?.mortgageDeals?.length ? bestMortgageDeals.mortgageDeals[0] : null;

  return {
    property: selectPortfolioItemPropertyAdapter(property),
    mortgage: selectPortfolioPropertyMortgageAdapter(mortgage),
    mortgageCase: selectPortfolioMortgageCaseAdapter(mortgageCase),
    smartSearches: filterSmartSearchByPropertyId(property.id, smartSearches),
    bestBetterDeal,
    casePreviewCard,
  };
};

const selectPortfolioMortgageCaseAdapter = (mortgageCase: HTTP_CP_MortgageCase): PropertiesPortfolioItemMortgageCase => {
  if (!mortgageCase) return null;
  return {
    id: mortgageCase.id,
    mortgageCaseStatus: mortgageCase.mortgageCaseStatus,
  };
};

const selectPortfolioItemPropertyAdapter = (property: HTTP_CP_Property): PropertiesPortfolioItemProperty => {
  if (!property) return null;
  return {
    id: property.id,
    name: property.name,
    value: property.roundedCurrentValue,
    investmentType: ENUM_PropertyInvestmentTypeMap.get(property.investmentType).abbreviation,
    type: property.propertyType,
    addressLines: property.addressLines,
    energyPerformanceCertificate: property.energyPerformanceCertificate,
  };
};

const selectPortfolioPropertyMortgageAdapter = (mortgage: HTTP_CP_Mortgage): PropertiesPortfolioItemMortgage => {
  if (!mortgage) return null;
  return {
    id: mortgage.id,
    outstandingBalance: mortgage.roundedCurrentOutstandingBalance,
    monthlyPayment: mortgage.currentMonthlyPayment,
    lenderLogo: mortgage.lenderLogo,
    numberOfMortgageParts: mortgage.mortgageParts.length,
    mortgageExpiryType: mortgage.mortgageExpiryType,
    termRemainingInMonths: mortgage.termRemaining,
    numberOfMonthsToLastEarlyRepaymentChargeEndDate: mortgage.numberOfMonthsToLastEarlyRepaymentChargeEndDate,
  };
};

const findMortgageCase = (mortgageId: string, mortgageCases: HTTP_CP_MortgageCase[]): HTTP_CP_MortgageCase => {
  return mortgageId ? mortgageCases.find((mortgageCase) => mortgageCase.mortgageId === mortgageId) : null;
};

const findBestDealsByMortgageId = (mortgageId: string, bestBetterDeals: BestBetterDealViewModel[]): BestBetterDealViewModel => {
  return mortgageId ? bestBetterDeals.find((bestBetterDeal) => bestBetterDeal.mortgageId === mortgageId) : null;
};

const filterSmartSearchByPropertyId = (propertyId: string, smartSearches: HTTP_CP_SmartSearch[]): HTTP_CP_SmartSearch[] => {
  if (!smartSearches?.length) return [];
  return smartSearches?.filter(
    (smartSearch) =>
      smartSearch.forRemortgageBorrowMore?.propertyId === propertyId ||
      smartSearch.forMortgageProduct?.propertyId === propertyId ||
      smartSearch.forRemortgagePayFaster?.propertyId === propertyId,
  );
};
